import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { QRCodeCanvas as QRCode } from "qrcode.react";
import { Form } from "react-bootstrap";
import printJS from "print-js";

import { textConstants } from "@/languages/textConstants";

import { agricolaName, dateToLocale, formatMilesDecimal, formatMilesDecimalNew, initialText, validRoles } from "@/services/Utils";
import api from "@/services/api.service";

import InventoryActions from "./InventoryActions";
import InventoryDownload from "./InventoryDownload";

import { updateInventories } from "@/redux/actions/inventory.actions";
import { CHANGE_SELECT_INVENTORIES } from "@/redux/types/inventory.types";

import CustomTooltip from "@/components/desktop/tooltip/CustomTooltip";
import Fetching from "@/components/Fetching/Fetching";
import usePrintInventoryLabel from "@/hooks/printer/zebraPrinter";
import { base64toBlob } from "@/hooks/printer/useBase64";

import classes from "./inventory.module.scss";

const InventoryTableDetail = ({ index, base, handleShowMovements }) => {
	const { customer } = useSelector((state) => state.auth.user);
	const { inventories, selected } = useSelector((state) => state.inventory);

	const { filter } = useSelector((state) => state.auth);
	const { estate_parent_id, estate_id, type_currency, search, parameter } = filter;

	const userRoles = JSON.parse(localStorage.getItem("roles"));
	const isManager = validRoles(userRoles, ["administrador", "gerente_agricola"]);

	const dispatch = useDispatch();

	const { printInventoryLabel, getZpl, isLoading } = usePrintInventoryLabel();

	const [isFetching, setIsFetching] = useState(true);

	useEffect(() => {
		setIsFetching(true);

		if (inventories[index].active && !Object.prototype.hasOwnProperty.call(inventories[index], "details")) {
			const newBase = { ...base, estate_parent_id, estate_id, type_currency, search, parameter };

			let sendInventory = { ...inventories[index] };
			sendInventory.INVENTORY_ID = "";

			api.get(`/client/inventories/detail`, { ...sendInventory, params: newBase }).then((response) => {
				const newInventories = [...inventories];
				newInventories[index].details = response.details;
				dispatch(updateInventories(newInventories));
				setIsFetching(false);
			});
		} else {
			setIsFetching(false);
		}
		// eslint-disable-next-line
	}, [inventories[index].active]);

	if (!inventories[index].active) {
		return null;
	}

	const checkInventory = (localInventory) => {
		const inventoryIds = localInventory.ID_INVENTARIO.toString()
			.split(",")
			.map((id) => id.trim());

		const areAllSelected = inventoryIds.every((inventoryId) => selected.some((s) => s.original_id === inventoryId));

		if (!areAllSelected) {
			const newSelected = [
				...selected,
				...inventoryIds
					.filter((inventoryId) => !selected.some((s) => s.original_id === inventoryId))
					.map((inventoryId) => ({
						original_id: inventoryId,
						is_generic: localInventory.CODIGO_PRODUCTO.startsWith("G") ? 1 : 0,
					})),
			];

			dispatch({
				type: CHANGE_SELECT_INVENTORIES,
				payload: { selected: newSelected },
			});
		} else {
			const newSelected = selected.filter((s) => !inventoryIds.includes(s.original_id));

			dispatch({
				type: CHANGE_SELECT_INVENTORIES,
				payload: { selected: newSelected },
			});
		}
	};

	const isSelected = (inventoryId) => {
		const allIdsInDetail = inventoryId.split(",").map((id) => Number(id));
		return allIdsInDetail.every((id) => selected.some((sel) => Number(sel.original_id) === Number(id)));
	};

	async function print_tag_inventory(detail) {
		const ids = [];
		ids.push(detail.ID_INVENTARIO);

		const response = await api
			.post("/client/inventories/create_tags_pdf", {
				ids,
				defaultUnit: base.defaultUnit,
			})
			.then((data) => {
				return data;
			});

		const blobUrl = URL.createObjectURL(base64toBlob(`data:application/pdf;base64,${response.url}`));

		printJS(blobUrl);
	}

	if (isFetching) {
		return (
			<tr>
				<td colSpan={customer.use_estates_parents ? 11 : 10} className={classes.td_table}>
					<Fetching topMargin={"0px"} />
				</td>
			</tr>
		);
	}

	return (
		<tr className={classes.table_detail}>
			<td colSpan={customer.use_estates_parents ? 11 : 10} className={classes.td_table}>
				<table className={classes.table_item}>
					<thead>
						<tr className={classes.table_item_tr}>
							<th className={classes.th_detail} style={{ width: "3%" }}></th>
							<th className={classes.th_detail} style={{ width: "5%" }}>
								Razón Social
							</th>
							<th className={classes.th_detail} style={{ width: "5%" }}>
								Cód.
							</th>
							<th className={classes.th_detail} style={{ width: "7%" }}>
								Contenido Actual
							</th>
							<th className={classes.th_detail} style={{ width: "6%" }}>
								U.M.
							</th>
							<th className={classes.th_detail} style={{ width: "7%" }}>
								N° Factura
							</th>
							<th className={classes.th_detail} style={{ width: "7%" }}>
								N° {textConstants[customer.country_id].delivery}
							</th>
							{isManager && (
								<th className={classes.th_detail} style={{ width: "6%" }}>
									Precio
								</th>
							)}
							<th className={classes.th_detail} style={{ width: "6%" }}>
								Lote de Producción
							</th>
							<th className={classes.th_detail} style={{ width: "10%" }}>
								Fecha Vencimiento
							</th>
							{!!customer.use_estates_parents && (
								<th className={classes.th_detail} style={{ width: "10%" }}>
									{customer.customer_screens.find((cs) => cs.screen_id === 38)
										? customer.customer_screens.find((cs) => cs.screen_id === 38).name
										: "Grupo Campo"}
								</th>
							)}
							<th className={classes.th_detail} style={{ width: "10%" }}>
								{customer.customer_screens.find((cs) => cs.screen_id === 51)
									? customer.customer_screens.find((cs) => cs.screen_id === 51).name
									: "Campo"}
							</th>
							<th className={classes.th_detail} style={{ width: "9%" }}>
								Observación
							</th>
							<th className={classes.th_detail} style={{ width: "5%" }}>
								QR
							</th>
							<th className={classes.th_detail} style={{ width: "3%" }}>
								Acción
							</th>
							<th className={classes.th_detail} style={{ width: "4%" }}>
								Descargable
							</th>
						</tr>
					</thead>

					<tbody>
						{inventories[index] &&
							inventories[index].details &&
							inventories[index].details.map((detail, i) => {
								return (
									<tr key={i} className={`${classes.table_item_tr} ${detail.EN_USO ? classes.in_use : ""}`}>
										<td className={classes.table_item_td}>
											<div className={`${classes.div_item} ${classes.first_div} ${detail.EN_USO ? classes.in_use : ""}`}>
												<Form.Check
													key={`inventory_${detail.ID_INVENTARIO}`}
													type="checkbox"
													checked={isSelected(detail.ID_INVENTARIO)}
													onChange={(_e) => checkInventory(detail)}
													onClick={(_e) => checkInventory(detail)}
												/>
											</div>
										</td>
										<td className={classes.table_item_td}>
											<div className={`${classes.div_item} ${detail.EN_USO ? classes.in_use : ""}`}>
												<CustomTooltip text={initialText(detail.RAZON_SOCIAL)} description={detail.RAZON_SOCIAL} />
											</div>
										</td>
										<td className={classes.table_item_td}>
											<div className={`${classes.div_item} ${detail.EN_USO ? classes.in_use : ""}`}>{`${detail.IDENTIFICADOR}`}</div>
										</td>
										<td className={classes.table_item_td}>
											<div className={`${classes.div_item} ${classes.number_value} ${detail.EN_USO ? classes.in_use : ""}`}>
												<div className={`${classes.stock_number}`} onClick={() => handleShowMovements(detail)}>
													<strong>{detail.STOCK ? formatMilesDecimalNew(detail.STOCK) : ""}</strong>
												</div>
											</div>
										</td>
										<td className={classes.table_item_td}>
											<div className={`${classes.div_item} ${detail.EN_USO ? classes.in_use : ""}`}>{detail.UNIDAD_DE_MEDIDA}</div>
										</td>
										<td className={classes.table_item_td}>
											<div className={`${classes.div_item} ${classes.number_value} ${detail.EN_USO ? classes.in_use : ""}`}>
												{detail.NUMERO_FACTURA}
											</div>
										</td>
										<td className={classes.table_item_td}>
											<div className={`${classes.div_item} ${classes.number_value} ${detail.EN_USO ? classes.in_use : ""}`}>
												{detail.NUMERO_GUIA_DE_DESPACHO}
											</div>
										</td>
										{isManager && (
											<td className={classes.table_item_td}>
												<div className={`${classes.div_item} ${classes.number_value} ${detail.EN_USO ? classes.in_use : ""}`}>
													{detail.PRECIO ? `$${formatMilesDecimal(detail.PRECIO)}` : ""}
												</div>
											</td>
										)}
										<td className={classes.table_item_td} style={{ width: "6%" }}>
											<div className={`${classes.div_item} ${detail.EN_USO ? classes.in_use : ""}`}>
												{detail.LOTE_PRODUCCION ? `${detail.LOTE_PRODUCCION}` : ""}
											</div>
										</td>
										<td className={classes.table_item_td}>
											<div className={`${classes.div_item} ${detail.EN_USO ? classes.in_use : ""}`}>
												{detail.FECHA_DE_VENCIMIENTO ? dateToLocale(detail.FECHA_DE_VENCIMIENTO) : ""}
											</div>
										</td>
										{!!customer.use_estates_parents && (
											<td className={classes.table_item_td}>
												<div className={`${classes.div_item} ${detail.EN_USO ? classes.in_use : ""}`}>
													<CustomTooltip
														text={agricolaName(detail.GRUPOS_CAMPOS ? detail.GRUPOS_CAMPOS : "")}
														description={detail.GRUPOS_CAMPOS ? detail.GRUPOS_CAMPOS : ""}
													/>
												</div>
											</td>
										)}
										<td className={classes.table_item_td}>
											<div
												className={`${classes.div_item} ${detail.EN_USO ? classes.in_use : ""}`}
												style={{
													fontSize: "12px",
													minHeight: "52px",
												}}
											>
												<CustomTooltip
													text={agricolaName(detail.CAMPOS ? detail.CAMPOS : "")}
													description={detail.CAMPOS ? detail.CAMPOS : ""}
												/>
											</div>
										</td>
										<td className={classes.table_item_td}>
											<div
												className={`${classes.div_item} ${detail.EN_USO ? classes.in_use : ""}`}
												style={{
													fontSize: "12px",
													minHeight: "52px",
												}}
											>
												{detail.OBSERVACIONES ? agricolaName(detail.OBSERVACIONES) : ""}
											</div>
										</td>
										<td className={classes.table_item_td}>
											<div className={`${classes.div_item} ${detail.EN_USO ? classes.in_use : ""}`}>
												{detail.IDENTIFICADOR ? (
													<QRCode
														value={`${detail.ID_INVENTARIO}`}
														size={35}
														style={{
															marginTop: "6px",
															cursor: "pointer",
														}}
														onClick={() => {
															if (customer.zpl_printer_configured) {
																if (!isLoading) {
																	let zplList = [];
																	const zpl = getZpl({
																		productName: detail.NOMBRE_COMERCIAL,
																		code: detail.IDENTIFICADOR,
																		id: detail.ID_INVENTARIO,
																		content: detail.STOCK,
																		unit_measurement: detail.UNIDAD_DE_MEDIDA,
																		lot: detail.LOTE,
																		expirationDate: detail.FECHA_DE_VENCIMIENTO,
																		businessName: detail.RAZON_SOCIAL,
																	});
																	zplList.push(zpl);
																	printInventoryLabel(zplList);
																}
															} else {
																print_tag_inventory(detail);
															}
														}}
													/>
												) : (
													""
												)}
											</div>
										</td>
										<td
											className={classes.table_item_td}
											style={{
												position: "relative",
											}}
										>
											<div className={`${classes.div_item} ${detail.EN_USO ? classes.in_use : ""}`}>
												<InventoryActions
													base={base}
													detail={detail}
													print_tag_inventory={print_tag_inventory}
													index={index}
													index_detail={i}
													handleShowMovements={handleShowMovements}
												/>
											</div>
										</td>
										<td
											className={classes.table_item_td}
											style={{
												position: "relative",
											}}
										>
											<div className={`${classes.div_item} ${classes.last_div} ${detail.EN_USO ? classes.in_use : ""}`}>
												<InventoryDownload detail={detail} index={index} index_detail={i} />
											</div>
										</td>
									</tr>
								);
							})}
					</tbody>
				</table>
			</td>
		</tr>
	);
};

export default InventoryTableDetail;
